import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api, { apiErrorMessage } from '../services/api';
import FloatingInput from '../components/ui/FloatingInput';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await api.post('/api/auth/forgot-password', { email: email.trim() });
      // The API deliberately answers the same way whether or not the address is
      // registered, so we must not imply that an account was found.
      setSent(true);
    } catch (err) {
      setError(apiErrorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-50 flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-soft-md border border-surface-100 p-7">
          {sent ? (
            <>
              <div className="w-12 h-12 rounded-2xl bg-primary-50 text-primary-600 flex items-center justify-center mb-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
              </div>
              <h1 className="text-h2 text-surface-900 mb-2">Check your email</h1>
              <p className="text-body text-surface-600 mb-6">
                If <span className="font-medium text-surface-800">{email.trim()}</span> is
                registered, we&apos;ve sent a link to reset your password. It expires in one hour.
              </p>
              <p className="text-body-sm text-surface-500 mb-6">
                Didn&apos;t get anything? Check your spam folder, or{' '}
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  className="font-semibold text-primary-600 hover:text-primary-700"
                >
                  try a different address
                </button>
                .
              </p>
              <Link
                to="/login"
                className="block w-full text-center px-5 py-3 bg-primary-500 text-white text-body font-semibold rounded-xl hover:bg-primary-600 transition-colors duration-200"
              >
                Back to sign in
              </Link>
            </>
          ) : (
            <>
              <h1 className="text-h2 text-surface-900 mb-2">Reset your password</h1>
              <p className="text-body text-surface-600 mb-6">
                Enter the email address on your account and we&apos;ll send you a link to set a
                new password.
              </p>

              {error && (
                <div className="mb-4 rounded-xl bg-error-50 border border-error-500/20 px-4 py-3">
                  <p className="text-body-sm text-error-600">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <FloatingInput
                  id="email"
                  name="email"
                  type="email"
                  label="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-5 py-3 bg-primary-500 text-white text-body font-semibold rounded-xl hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {submitting ? 'Sending…' : 'Send reset link'}
                </button>
              </form>

              <p className="text-body-sm text-surface-500 mt-6 text-center">
                Remembered it?{' '}
                <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default ForgotPassword;
